import { ArrayUtil } from "./ArrayUtil";

export type LoginListener = (isAuthenticated: boolean) => void;
export type BeforeLogoutListener = () => Promise<any> | void;

export interface TokenClaims {
    exp: number;
    final_exp: number;
    sub: string;
    company_id?: string;
    userKey?: number;
    tenant_id?: string;
    keep_signed_in?: boolean;
}

export class AuthToken {
    private static STORAGE_LOGIN_TOKEN_KEY = "mcleod-auth-claims";
    private static loginListeners: LoginListener[] = [];
    private static beforeLogoutListeners: BeforeLogoutListener[] = [];

    public static setTokenClaims(value: TokenClaims, keepSignedIn?: boolean) {
        keepSignedIn = keepSignedIn || value.keep_signed_in;
        const storage = keepSignedIn ? localStorage : sessionStorage;
        const unusedStorage = keepSignedIn ? sessionStorage : localStorage;
        if (value == null)
            storage.removeItem(AuthToken.STORAGE_LOGIN_TOKEN_KEY);
        else
            storage.setItem(AuthToken.STORAGE_LOGIN_TOKEN_KEY, JSON.stringify(value));
        unusedStorage.removeItem(AuthToken.STORAGE_LOGIN_TOKEN_KEY);
        AuthToken.loginListeners.forEach(listener => listener(value != null));
    }

    public static clear() {
        AuthToken.getStorages().forEach(storage => storage.removeItem(AuthToken.STORAGE_LOGIN_TOKEN_KEY));
        AuthToken.loginListeners.forEach(listener => listener(false));
    }

    public static getTokenClaims(): TokenClaims {
        let result = sessionStorage.getItem(AuthToken.STORAGE_LOGIN_TOKEN_KEY);
        if (result == null)
            result = localStorage.getItem(AuthToken.STORAGE_LOGIN_TOKEN_KEY);
        return result ? JSON.parse(result) : null;
    }

    private static getStorages(keepSignedIn?: boolean): Storage[] {
        const storages = [localStorage, sessionStorage];
        if (keepSignedIn === false)
            storages.splice(0, 1);
        else if (keepSignedIn === true)
            storages.splice(1, 1);
        return storages;
    }

    private static isPast(expiration: number): boolean {
        return expiration * 1000 < Date.now() - 120000; // add a 2 minute buffer to account for clock skew
    }

    public static isAuthenticated(): boolean {
        const claims = AuthToken.getTokenClaims();
        return claims != null && !AuthToken.isPast(claims?.exp) && !AuthToken.isPast(claims?.final_exp);
    }

    public static addLoginListener(listener: LoginListener) {
        AuthToken.loginListeners.push(listener);
    }

    public static removeLoginListener(listener: LoginListener) {
        ArrayUtil.removeFromArray(AuthToken.loginListeners, listener);
    }

    public static addBeforeLogoutListener(listener: BeforeLogoutListener) {
        AuthToken.beforeLogoutListeners.push(listener);
    }

    public static removeBeforeLogoutListener(listener: BeforeLogoutListener) {
        ArrayUtil.removeFromArray(AuthToken.beforeLogoutListeners, listener);
    }

    public static async fireBeforeLogoutListeners() {
        const promises = AuthToken.beforeLogoutListeners.map(listener => listener());
        await Promise.all(promises);
    }
}